import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { projectApiUrl } from "../../constants";
import { AuthContext } from "../../contexts/AuthContext";
import type { LayoutOutletContext } from "../../components/Layout/Layout";
import { formatUserDateTime } from "./dateTime.utils";
import { useButtonTitles } from "./useButtonTitles";
import "./PersonalData.css";

const ITEMS_PER_PAGE = 20;

const formatField = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const buildDeviceName = (device: Record<string, any>, fallback: string) => {
  const parts = [device.name, device.browser, device.os].filter(Boolean);
  return parts.length > 0 ? parts.join(" • ") : fallback;
};

const getDeviceStatusLabel = (status: string | undefined, t: (key: string, options?: Record<string, unknown>) => string) => {
  if (status === "logged_out") {
    return t("personalData.activeDevices.status.loggedOut", { defaultValue: "Logged out" });
  }
  if (status === "expired") {
    return t("personalData.activeDevices.status.expired", { defaultValue: "Expired" });
  }
  return t("personalData.activeDevices.status.active", { defaultValue: "Active" });
};

// docs/security-and-platform-work-plan.md Этап 7 — the access token is an
// HttpOnly cookie now; requests to this app's own backend (same-origin,
// withCredentials below) send it automatically. This backend in turn
// proxies to api.id.express server-to-server (see
// api-pd.id.express/backend/app/api/active_devices.py) since the browser
// can no longer send this app's cookie to a different host.
const AUTH_HEADERS: Record<string, string> = { "X-Requested-With": "XMLHttpRequest" };

const ActiveDevices: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { handleLogOut } = useContext(AuthContext);
  const { theme } = useOutletContext<LayoutOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const backToSecurityLabel = t("personalData.activeDevices.backToSecurity", { defaultValue: "Back to security" });
  const buttonTitlesRef = useButtonTitles(backToSecurityLabel);

  const currentPage = useMemo(() => {
    const page = Number(searchParams.get("current_page") || searchParams.get("page") || "1");
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }, [searchParams]);

  const endLocalSession = async () => {
    await handleLogOut({});
    navigate("/", { replace: true });
  };

  const fetchDevices = async (page: number) => {
    if (!projectApiUrl) {
      setError(t("personalData.activeDevices.error.noApiUrl", { defaultValue: "API URL is not configured." }));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${projectApiUrl.replace(/\/$/, "")}/active_devices`, {
        headers: AUTH_HEADERS,
        params: {
          current_page: page,
          items_per_page: ITEMS_PER_PAGE,
        },
        withCredentials: true,
      });

      const payload = response.data || {};
      setDevices(Array.isArray(payload.data) ? payload.data : []);
      setCount(typeof payload.count === "number" ? payload.count : 0);
      setActiveCount(typeof payload.active_count === "number" ? payload.active_count : 0);
      setTotalPages(typeof payload.total_pages === "number" ? payload.total_pages : 0);
    } catch (fetchError) {
      if (axios.isAxiosError(fetchError) && fetchError.response?.status === 401) {
        await endLocalSession();
        return;
      }
      const message = axios.isAxiosError(fetchError)
        ? fetchError.response?.data?.message || fetchError.message
        : String(fetchError);
      setError(String(message || "Failed to load active devices."));
      setDevices([]);
      setCount(0);
      setActiveCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices(currentPage);
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setSearchParams({ current_page: String(page) });
  };


  const handleRevokeDevice = async (session: Record<string, any>) => {
    const sessionId = String(session._id || "").trim();
    if (!sessionId || !projectApiUrl) {
      setActionError(t("personalData.activeDevices.error.revokeMissingSession", { defaultValue: "Device session cannot be revoked." }));
      return;
    }

    const confirmed = window.confirm(
      t("personalData.activeDevices.confirmRevoke", { defaultValue: "End this device session? The device will need to sign in again." })
    );
    if (!confirmed) return;

    setRevokingSessionId(sessionId);
    setActionMessage(null);
    setActionError(null);

    try {
      const response = await axios.delete(`${projectApiUrl.replace(/\/$/, "")}/active_devices/revoke`, {
        data: { sessionId },
        headers: AUTH_HEADERS,
        withCredentials: true,
      });
      setActionMessage(t("personalData.activeDevices.revokeSuccess", { defaultValue: "Device session ended." }));
      if (session.isCurrent || response.data?.isCurrent) {
        await endLocalSession();
        return;
      }
      setDevices((current) => current.filter((item) => String(item._id || "") !== sessionId));
      setCount((current) => Math.max(0, current - 1));
      setActiveCount((current) => Math.max(0, current - 1));
    } catch (revokeError) {
      if (session.isCurrent && axios.isAxiosError(revokeError) && revokeError.response?.status === 401) {
        await endLocalSession();
        return;
      }
      const message = axios.isAxiosError(revokeError)
        ? revokeError.response?.data?.message || revokeError.message
        : String(revokeError);
      setActionError(String(message || t("personalData.activeDevices.error.revokeFailed", { defaultValue: "Failed to end device session." })));
    } finally {
      setRevokingSessionId(null);
    }
  };

  return (
    <div ref={buttonTitlesRef} className={`pd-shell ${theme === "dark" ? "pd-theme-dark" : "pd-theme-light"}`} data-theme={theme}>
      <div className="pd-shell__noise" />
      <div className="pd-shell__ambient pd-shell__ambient--one" />
      <div className="pd-shell__ambient pd-shell__ambient--two" />

      <div className="pd-dashboard">
        <section className="pd-form-shell pd-glass pd-active-devices-panel">
          <div className="pd-form-shell__header">
            <div>
              <h2>{t("personalData.activeDevices.title", { defaultValue: "Active devices" })}</h2>
              <p>
                {t("personalData.activeDevices.description", {
                  defaultValue: "Review devices and sessions that currently have access to your account.",
                })}
              </p>
            </div>
            <div className="pd-topbar__actions">
              <button type="button" className="pd-secondary-button" title={backToSecurityLabel} onClick={() => navigate(`/${i18n.language}/personal_data?section=security`)}>
                {backToSecurityLabel}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="pd-shimmer-stack" aria-hidden="true">
              <div className="pd-shimmer pd-shimmer--hero" />
              <div className="pd-shimmer pd-shimmer--line" />
              <div className="pd-shimmer pd-shimmer--line" />
              <div className="pd-shimmer pd-shimmer--line short" />
            </div>
          ) : error ? (
            <div className="pd-notice-banner pd-notice-banner--error">{error}</div>
          ) : (
            <>
              {actionMessage && <div className="pd-notice-banner pd-notice-banner--success">{actionMessage}</div>}
              {actionError && <div className="pd-notice-banner pd-notice-banner--error">{actionError}</div>}

              <div className="pd-activity-logs-summary">
                <span>{t("personalData.activeDevices.summary", { defaultValue: "Active sessions" })}</span>
                <span>{`${activeCount} ${t("personalData.activeDevices.activeEntries", { defaultValue: "active" })} / ${count} ${t("personalData.activeDevices.entries", { defaultValue: "devices" })}`}</span>
              </div>

              {devices.length === 0 ? (
                <div className="pd-empty-state">
                  <p>{t("personalData.activeDevices.empty", { defaultValue: "No active devices found." })}</p>
                </div>
              ) : (
                <div className="pd-active-devices-list">
                  {devices.map((session, index) => {
                    const device = session.device || {};
                    return (
                      <article className="pd-active-device-card" key={`${session._id || session.deviceId || index}`}>
                        <div className="pd-active-device-card__header">
                          <div>
                            <strong>{buildDeviceName(device, t("personalData.activeDevices.unknownDevice", { defaultValue: "Unknown device" }))}</strong>
                            <p>{formatField(device.userAgent)}</p>
                          </div>
                          <div className="pd-active-device-card__badges">
                            <span className={`pd-active-device-card__badge pd-active-device-card__badge--${session.status || "active"}`}>
                              {getDeviceStatusLabel(session.status, t)}
                            </span>
                            {session.isCurrent && (
                              <span className="pd-active-device-card__badge">
                                {t("personalData.activeDevices.currentDevice", { defaultValue: "Current device" })}
                              </span>
                            )}
                            {session.isActive && (
                              <button
                                type="button"
                                className="pd-active-device-card__revoke"
                                onClick={() => handleRevokeDevice(session)}
                                disabled={revokingSessionId === String(session._id || "")}
                              >
                                {revokingSessionId === session._id
                                  ? t("personalData.activeDevices.revoking", { defaultValue: "Ending..." })
                                  : t("personalData.activeDevices.revoke", { defaultValue: "End session" })}
                              </button>
                            )}
                          </div>
                        </div>
                        <dl className="pd-active-device-card__details">
                          <div>
                            <dt>{t("personalData.activeDevices.fields.deviceId", { defaultValue: "Device ID" })}</dt>
                            <dd>{formatField(session.deviceId)}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.appId", { defaultValue: "Application" })}</dt>
                            <dd>{formatField(session.appId)}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.ipAddress", { defaultValue: "IP" })}</dt>
                            <dd>{formatField(device.ipAddress)}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.type", { defaultValue: "Type" })}</dt>
                            <dd>{formatField(device.type)}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.browser", { defaultValue: "Browser" })}</dt>
                            <dd>{formatField([device.browser, device.browserVersion].filter(Boolean).join(" "))}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.os", { defaultValue: "OS" })}</dt>
                            <dd>{formatField([device.os, device.osVersion].filter(Boolean).join(" "))}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.firstSeenAt", { defaultValue: "First seen" })}</dt>
                            <dd>{formatUserDateTime(device.firstSeenAt, i18n.language)}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.lastSeenAt", { defaultValue: "Last seen" })}</dt>
                            <dd>{formatUserDateTime(device.lastSeenAt || session.refreshTokenIatAt, i18n.language)}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.accessTokenExpAt", { defaultValue: "Access expires" })}</dt>
                            <dd>{formatUserDateTime(session.accessTokenExpAt, i18n.language)}</dd>
                          </div>
                          <div>
                            <dt>{t("personalData.activeDevices.fields.expiresAt", { defaultValue: "Session expires" })}</dt>
                            <dd>{formatUserDateTime(session.refreshTokenExpAt, i18n.language)}</dd>
                          </div>
                        </dl>
                      </article>
                    );
                  })}
                </div>
              )}

              {totalPages > 1 && (
                <div className="pd-pagination">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`pd-secondary-button ${page === currentPage ? "is-active" : ""}`}
                      onClick={() => handlePageChange(page)}
                      disabled={page === currentPage}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ActiveDevices;
